import React from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Define all menu items
  const allMenuItems = [
    { 
      title: 'Monitoring', 
      icon: 'bi-activity', 
      path: '/admin/monitoring',
      description: 'Dashboard di monitoring sistema',
      color: '#dc3545',
      roles: ['admin'] // Visible to admin only
    },
    { 
      title: 'Dashboard Tesserati', 
      icon: 'bi-people-fill', 
      path: '/admin/membership-dashboard',
      description: 'Statistiche complete tesserati',
      color: '#17a2b8',
      roles: ['admin', 'sda_regional', 'sda_territorial'] // Visibile ad admin e SDA regionali/territoriali
    },
    { 
      title: 'Dashboard SDA', 
      icon: 'bi-graph-up', 
      path: '/admin/sda-dashboard',
      description: 'Statistiche tesseramenti per disciplina',
      color: '#9c27b0',
      roles: ['admin', 'sda_territorial', 'sda_regional', 'sda_nazionale'] // Visible to SDA roles and admin
    },
    { 
      title: 'Gestisci utenti', 
      icon: 'bi-people', 
      path: '/admin/user',
      description: 'Crea e gestisci utenti del sistema',
      color: '#006e39',
      roles: ['admin', 'uisp_national', 'uisp_regional'] // NOT visible to uisp_territorial and SDA
    },
    { 
      title: 'Gestisci eventi', 
      icon: 'bi-calendar-event', 
      path: '/admin/events',
      description: 'Crea e gestisci eventi sportivi',
      color: '#0077b6',
      roles: ['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial', 'sda_territorial', 'sda_regional', 'sda_nazionale', 'cordinator_territorial', 'cordinator_regional', 'cordinator_nazionale'] // Visible to all committee roles
    },
    { 
      title: 'Lista eventi', 
      icon: 'bi-list-ul', 
      path: '/admin/events/list',
      description: 'Visualizza tutti gli eventi',
      color: '#2a9d8f',
      roles: ['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial', 'sda_territorial', 'sda_regional', 'sda_nazionale', 'cordinator_territorial', 'cordinator_regional', 'cordinator_nazionale'] // Visible to all committee roles
    },
    { 
      title: 'Codici associazione', 
      icon: 'bi-card-checklist', 
      path: '/admin/association-codes',
      description: 'Visualizza codici associazioni',
      color: '#e76f51',
      roles: ['admin'] // Visible to admin only
    },
    { 
      title: 'Sync UISP', 
      icon: 'bi-cloud-download', 
      path: '/admin/uisp-sync',
      description: 'Sincronizza tesserati da UISP',
      color: '#5c6bc0',
      roles: ['admin'] // Visible to admin only
    },
    {
      title: 'Gestione Pagamenti',
      icon: 'bi-credit-card-2-front',
      path: '/admin/payments',
      description: 'Visualizza e gestisci pagamenti e richieste',
      color: '#1976d2',
      roles: ['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial']
    },
    {
      title: 'Conti Bancari',
      icon: 'bi-bank',
      path: '/admin/bank-accounts',
      description: 'Configura IBAN per pagamenti via bonifico',
      color: '#388e3c',
      roles: ['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial']
    },
    {
      title: 'Stripe Connect',
      icon: 'bi-lightning-charge',
      path: '/admin/stripe-onboarding',
      description: 'Collega account Stripe per pagamenti online',
      color: '#6772e5',
      roles: ['admin']
    }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="row p-2 p-md-4">
        <div className="col-12">
          <div className="glass-container p-3 p-md-4 rounded-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            
            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
              <div>
                <h1 style={{ color: "#006e39" }} className="mb-2 fs-3 fs-md-1">
                  <i className="bi bi-speedometer2 me-3"></i>
                  Dashboard Amministratore
                </h1>
                <p className="text-muted mb-0">
                  <i className="bi bi-person-badge me-2"></i>
                  {user?.email || 'Admin'} - <strong>{user?.role || 'admin'}</strong>
                </p>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="alert alert-success d-flex align-items-center mb-4" role="alert" style={{ 
              backgroundColor: 'rgba(0, 110, 57, 0.1)', 
              borderColor: '#006e39',
              color: '#006e39'
            }}>
              <i className="bi bi-shield-check me-3 fs-4"></i>
              <div>
                <strong>Benvenuto nella Dashboard di gestione!</strong>
              </div>
            </div>

            {/* Main Menu Cards */}
            <h3 style={{ color: "#006e39" }} className="mb-4">
              <i className="bi bi-gear-fill me-2"></i>
              Funzioni Amministrative
            </h3>

            <div className="row g-4">
              {menuItems.map((item, index) => (
                <div key={index} className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="glass-card p-4 rounded-4 h-100"
                    style={{
                      background: '#ffffff',
                      border: '2px solid ' + item.color,
                      boxShadow: `0 2px 8px ${item.color}20`,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => navigate(item.path)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.boxShadow = `0 8px 16px ${item.color}40`;
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.boxShadow = `0 2px 8px ${item.color}20`;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: `${item.color}15` }}>
                        <i className={`bi ${item.icon} fs-2`} style={{ color: item.color }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-2" style={{ color: item.color, fontWeight: '600' }}>
                          {item.title}
                        </h5>
                        <p className="text-muted small mb-0">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Stats Section */}
            <div className="mt-5">
              <h4 style={{ color: "#006e39" }} className="mb-3">
                <i className="bi bi-graph-up me-2"></i>
                Accesso Rapido
              </h4>
              <div className="row g-3">
                <div className="col-12 col-sm-6 col-md-4">
                  <div className="card border-0 shadow-sm" style={{ 
                    background: 'linear-gradient(135deg, #006e39, #00a86b)',
                    color: 'white'
                  }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="text-white-50 mb-1">Crea Nuovo</h6>
                          <h4 className="mb-0">Evento</h4>
                        </div>
                        <div 
                          className="btn btn-light rounded-circle p-3"
                          onClick={() => navigate('/admin/events/create')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="bi bi-plus-lg fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <div className="card border-0 shadow-sm" style={{ 
                    background: 'linear-gradient(135deg, #0077b6, #48cae4)',
                    color: 'white'
                  }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="text-white-50 mb-1">Visualizza</h6>
                          <h4 className="mb-0">Eventi</h4>
                        </div>
                        <div 
                          className="btn btn-light rounded-circle p-3"
                          onClick={() => navigate('/admin/events/list')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="bi bi-calendar-check fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Card "Gestisci Utenti" visibile solo per admin, uisp_national e uisp_regional */}
                {user && (user.role === 'admin' || user.role === 'uisp_national' || user.role === 'uisp_regional') && (
                  <div className="col-12 col-sm-6 col-md-4">
                    <div className="card border-0 shadow-sm" style={{ 
                      background: 'linear-gradient(135deg, #e76f51, #f4a261)',
                      color: 'white'
                    }}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-white-50 mb-1">Gestisci</h6>
                            <h4 className="mb-0">Utenti</h4>
                          </div>
                          <div 
                            className="btn btn-light rounded-circle p-3"
                            onClick={() => navigate('/admin/user')}
                            style={{ cursor: 'pointer' }}
                          >
                            <i className="bi bi-person-plus fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Card "Pagamenti" visibile per admin e ruoli territoriali */}
                {user && ['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial'].includes(user.role) && (
                  <div className="col-12 col-sm-6 col-md-4">
                    <div className="card border-0 shadow-sm" style={{ 
                      background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                      color: 'white'
                    }}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-white-50 mb-1">Gestisci</h6>
                            <h4 className="mb-0">Pagamenti</h4>
                          </div>
                          <div 
                            className="btn btn-light rounded-circle p-3"
                            onClick={() => navigate('/admin/payments')}
                            style={{ cursor: 'pointer' }}
                          >
                            <i className="bi bi-credit-card-2-front fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;